/**
 * Canonical enum values that mirror the Drizzle `pgEnum` declarations in
 * `@webbox/db-platform`. These are the values the DB accepts; the labels are
 * what TypeScript callers use. Kept in sync by design — any change here must
 * ship with a migration in `packages/db-platform`.
 *
 * Source of truth:
 *   - docs/technical/db-schema-platform.md § "ENUM 定義"
 *   - docs/design/tenant-lifecycle.md § "状態モデル"
 *   - docs/design/permission-model.md
 */

export const TenantStatus = {
  Provisioning: 'provisioning',
  Active: 'active',
  PastDue: 'past_due',
  Suspended: 'suspended',
  Cancelling: 'cancelling',
  Cancelled: 'cancelled',
  PurgeScheduled: 'purge_scheduled',
  Purged: 'purged',
  Failed: 'failed',
} as const;
export type TenantStatus = (typeof TenantStatus)[keyof typeof TenantStatus];

export const TENANT_STATUS_VALUES = Object.values(TenantStatus) as readonly TenantStatus[];

export const PlanName = {
  Light: 'light',
  Medium: 'medium',
  Heavy: 'heavy',
  Platinum: 'platinum',
} as const;
export type PlanName = (typeof PlanName)[keyof typeof PlanName];

export const PLAN_NAME_VALUES = Object.values(PlanName) as readonly PlanName[];

export const PlatformRole = {
  SuperAdmin: 'super_admin',
  Operator: 'operator',
  Developer: 'developer',
  // F16 — assigned to tenant owners by the provisioning saga. Ranked below
  // `developer` (see apps/admin/src/lib/server/guards.ts §PLATFORM_ROLE_RANK)
  // so it can never satisfy the `operator` minimum that /dashboard requires.
  TenantOwner: 'tenant_owner',
} as const;
export type PlatformRole = (typeof PlatformRole)[keyof typeof PlatformRole];

export const PLATFORM_ROLE_VALUES = Object.values(PlatformRole) as readonly PlatformRole[];

export const TenantRole = {
  Owner: 'owner',
  Admin: 'admin',
  Editor: 'editor',
  Contributor: 'contributor',
} as const;
export type TenantRole = (typeof TenantRole)[keyof typeof TenantRole];

export const TENANT_ROLE_VALUES = Object.values(TenantRole) as readonly TenantRole[];
export const TENANT_ROLES = ['owner', 'admin', 'editor', 'contributor'] as const;

export const SubscriptionStatus = {
  Trialing: 'trialing',
  Active: 'active',
  PastDue: 'past_due',
  Unpaid: 'unpaid',
  Canceled: 'canceled',
  Incomplete: 'incomplete',
  IncompleteExpired: 'incomplete_expired',
  Paused: 'paused',
} as const;
export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

export const SUBSCRIPTION_STATUS_VALUES = Object.values(
  SubscriptionStatus,
) as readonly SubscriptionStatus[];

export const BillingCycle = {
  Monthly: 'monthly',
  Annual: 'annual',
} as const;
export type BillingCycle = (typeof BillingCycle)[keyof typeof BillingCycle];

export const PublishStatus = {
  Draft: 'draft',
  Published: 'published',
  Scheduled: 'scheduled',
  Archived: 'archived',
} as const;
export type PublishStatus = (typeof PublishStatus)[keyof typeof PublishStatus];

export const PUBLISH_STATUSES = ['draft', 'published', 'scheduled', 'archived'] as const;

export const FormFieldType = {
  Text: 'text',
  Email: 'email',
  Tel: 'tel',
  Textarea: 'textarea',
  Select: 'select',
  Checkbox: 'checkbox',
  Radio: 'radio',
  File: 'file',
} as const;
export type FormFieldType = (typeof FormFieldType)[keyof typeof FormFieldType];

export const FORM_FIELD_TYPES = [
  'text',
  'email',
  'tel',
  'textarea',
  'select',
  'checkbox',
  'radio',
  'file',
] as const;

export const ActorType = {
  TenantUser: 'tenant_user',
  PlatformUser: 'platform_user',
  ApiKey: 'api_key',
  System: 'system',
} as const;
export type ActorType = (typeof ActorType)[keyof typeof ActorType];

export const ACTOR_TYPES = ['tenant_user', 'platform_user', 'api_key', 'system'] as const;

export const NavigationSlug = {
  Header: 'header',
  Footer: 'footer',
  Drawer: 'drawer',
} as const;
export type NavigationSlug = (typeof NavigationSlug)[keyof typeof NavigationSlug];

export const NAVIGATION_SLUGS = ['header', 'footer', 'drawer'] as const;

export const FormSubmissionStatus = {
  Received: 'received',
  Read: 'read',
  Archived: 'archived',
  Spam: 'spam',
} as const;
export type FormSubmissionStatus = (typeof FormSubmissionStatus)[keyof typeof FormSubmissionStatus];

export const FORM_SUBMISSION_STATUSES = ['received', 'read', 'archived', 'spam'] as const;
